import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spacer from "../components/ui/Spacer";
import LoadingBouncyDots from "../components/ui/LoadingBouncyDots";
import ActivityCardFromFeedItem from "../components/result/ActivityCardFromFeedItem";
import { useUserDoc } from "../hooks/useUserDoc";
import { useActivityFeed } from "../context/ActivityFeedContext";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { useAccessControl } from "../hooks/useAccessControl";
import { useAuth } from "../context/AuthContext";
import SaveProgressBanner from "../components/auth/SaveProgressBanner";
import ClickableCard from "../components/ui/ClickableCard";
import { PLAN } from "../utils/planRules"; // Import PLAN constant

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { loading: loadingDoc, data } = useUserDoc();
  const {
    loading: loadingFeed,
    error: feedError,
    items,
    refreshNow,
  } = useActivityFeed();

  const { lockResults, plan, totalAny } = useAccessControl();

  // If the user has a plan (including FREE), they should be able to click the activity card
  // and be taken to the activity page where locks are applied.
  // Only PLAN.NONE (pre-signup/first-recording) should block opening the activity.
  const canOpenActivity = plan !== PLAN.NONE;

  // Pull-to-refresh on the app scroll container
  const [containerEl, setContainerEl] = useState(null);
  useEffect(() => setContainerEl(document.getElementById("app-scroll")), []);
  const { offset, isRefreshing } = usePullToRefresh({
    container: containerEl,
    onRefresh: refreshNow,
    threshold: 72,
    maxPull: 140,
  });

  useEffect(() => {
    refreshNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAGE_SIZE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const hasAnyRecordings = !loadingFeed && !feedError && items.length > 0;
  // Sentinel for infinite scroll
  const sentinelRef = useRef(null);
  // Prevent rapid multi-fires as the sentinel toggles visibility
  const loadingMoreRef = useRef(false);

  const visibleItems = useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount]
  );
  const canLoadMore = visibleCount < items.length;

  // Auto "load more" when sentinel enters view (near bottom)
  useEffect(() => {
    if (!containerEl || !sentinelRef.current) return;
    if (!canLoadMore) return; // nothing more to show

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !loadingMoreRef.current) {
          loadingMoreRef.current = true;
          // small microtask delay to batch state updates during fast scroll
          Promise.resolve().then(() => {
            setVisibleCount((n) => Math.min(n + PAGE_SIZE, items.length));
            // allow subsequent loads next tick
            setTimeout(() => {
              loadingMoreRef.current = false;
            }, 50);
          });
        }
      },
      {
        // Observe within the scrolling container (your #app-scroll)
        root: containerEl,
        // Start loading a bit before the user hits the bottom
        rootMargin: "200px 0px 200px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [containerEl, canLoadMore, items.length, PAGE_SIZE]);

  const isAnon = !!user?.isAnonymous;

  // console.log(visibleItems);

  return (
    <div className="relative w-full max-w-lg h-fit flex flex-col p-4 gap-4 z-10 mx-auto">
      {/* Pull-to-refresh indicator */}
      <div
        className="pointer-events-none -mt-4 flex w-full items-end justify-center"
        style={{
          height: Math.max(0, offset),
          transition: offset === 0 ? "height 160ms ease-out" : "none",
        }}
        aria-hidden="true"
      >
        {(offset > 0 || isRefreshing) && (
          <LoadingBouncyDots
            text={
              isRefreshing
                ? t("activity.refreshing", { defaultValue: "Refreshing…" })
                : t("activity.pullToRefresh", {
                    defaultValue: "Pull to refresh",
                  })
            }
            size={6}
            gap={3}
            speed={700}
            className="mb-2"
            textClassName="text-xs text-typography/60"
          />
        )}
      </div>

      {/* Anonymous user save-progress banner */}
      {isAnon && (
        <>
          <SaveProgressBanner hasAnyRecordings={hasAnyRecordings} />
          <Spacer />
        </>
      )}
      {/* Empty state */}
      {!hasAnyRecordings && !loadingFeed && !feedError && (
        <>
          <div className="rounded-2xl flex flex-col gap-4 h-fit items-start">
            <div>
              <h3 className="subtitle-text">
                {t("emptyState.title", { defaultValue: "Try your first test" })}
              </h3>
              <p className="text-sm text-typography/80">
                {t("emptyState.copy", {
                  defaultValue:
                    "No recordings yet. Head to the recording page to get started.",
                })}
              </p>
            </div>
            <div className="flex items-center gap-2 justify-end w-full">
              <Link
                to="/recording"
                className="primary-button"
                aria-label={t("emptyState.ctaAria", {
                  defaultValue: "Go to recording to try the product",
                })}
              >
                {t("emptyState.cta", { defaultValue: "Record" })}
              </Link>
            </div>
          </div>
          <Spacer />
        </>
      )}

      {plan === PLAN.NONE && totalAny > 0 && (
        <>
          <div className="rounded-2xl flex flex-col gap-4 h-fit items-start">
            <h3 className="subtitle-text">
              {t("upsell.firstUsed.title", {
                defaultValue: "You're off to a great start",
              })}
            </h3>
            <p className="">
              {t("upsell.firstUsed.copy", {
                defaultValue:
                  "Choose a plan to keep recording and unlock insights.",
              })}
            </p>
            <div className="flex justify-end">
              <Link to="/payment" className="primary-button px-4 py-2 text-sm">
                {t("upsell.choosePlan", { defaultValue: "Choose a plan" })}
              </Link>
            </div>
          </div>
          <Spacer />
        </>
      )}

      {/* All recordings list */}
      {hasAnyRecordings &&
        visibleItems.map((item, idx) => {
          const key =
            (item.timestamp || 0) +
            "_" +
            (item.kind || "unknown") +
            "_" +
            (item.result?.name || item.metadataUrl || "");
          const isLast = idx === visibleItems.length - 1;
          return (
            <div key={key} className="flex flex-col h-fit gap-2 w-full">
              {/* Only PLAN.NONE users who have used their first test are blocked from navigating */}
              {canOpenActivity ? (
                <ClickableCard
                  to={`/home/activity?id=${encodeURIComponent(item.baseName)}`}
                  ariaLabel={`Open activity ${item.baseName}`}
                >
                  <ActivityCardFromFeedItem item={item} locked={lockResults} />
                </ClickableCard>
              ) : (
                <ActivityCardFromFeedItem item={item} locked={true} />
              )}
              {!isLast && <Spacer />}
            </div>
          );
        })}

      {/* Infinite scroll sentinel (in-view => load more) */}
      {hasAnyRecordings && canLoadMore && (
        <div ref={sentinelRef} aria-hidden="true" className="h-1 w-full" />
      )}

      {/* Load more */}
      {canLoadMore && (
        <button
          onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
          className="outlined-button self-center"
        >
          {t("activity.loadMore", { defaultValue: "Load more" })}
        </button>
      )}

      {/* Loading / errors */}
      {loadingFeed && (
        <LoadingBouncyDots
          text={t("activity.loadingLatest", {
            defaultValue: "Checking for latest…",
          })}
        />
      )}
      {feedError && (
        <div className="text-xs text-red-600">
          {t("activity.loadError", { defaultValue: "Could not load items." })}{" "}
          {String(feedError.message || feedError)}
        </div>
      )}
    </div>
  );
}

export default memo(HomePage);
